import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { apiKeysApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Key, 
  Loader2, 
  Copy, 
  Trash2, 
  Plus,
  Code,
  Terminal,
  Book,
  CheckCircle2,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

export default function ApiDocsPage() {
  const { language } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const response = await apiKeysApi.list();
      setApiKeys(response.data);
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel caricamento delle API keys' : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async () => {
    setCreating(true);
    try {
      const response = await apiKeysApi.create();
      setNewKeyValue(response.data.key);
      setShowKeyDialog(true);
      loadApiKeys();
      toast.success(language === 'it' ? 'API key creata con successo!' : 'API key created successfully!');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nella creazione della API key' : 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const deleteApiKey = async (id) => {
    try {
      await apiKeysApi.delete(id);
      toast.success(language === 'it' ? 'API key eliminata' : 'API key deleted');
      loadApiKeys();
      setKeyToDelete(null);
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nell\'eliminazione' : 'Failed to delete');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(language === 'it' ? 'Copiato negli appunti' : 'Copied to clipboard');
  };

  const baseUrl = window.location.origin + '/api/v1';

  const endpoints = [
    {
      method: 'POST',
      path: '/analyze',
      description: language === 'it' ? 'Crea un progetto e avvia l\'analisi' : 'Create a project and start analysis',
      params: [
        { name: 'prompt', type: 'string', required: true, description: language === 'it' ? 'Descrizione del progetto' : 'Project description' },
        { name: 'name', type: 'string', required: false, description: language === 'it' ? 'Nome del progetto' : 'Project name' },
        { name: 'language', type: 'string', required: false, default: '"it"', description: '"it" or "en"' },
        { name: 'team_size', type: 'string', required: false, default: '"medium"', description: '"small", "medium", "large", "custom"' },
        { name: 'execution_mode', type: 'string', required: false, default: '"automatic"', description: '"automatic" or "step_by_step"' },
        { name: 'analysis_mode', type: 'string', required: false, default: '"specialists"', description: '"specialists" or "comparison"' },
        { name: 'wait_for_completion', type: 'boolean', required: false, default: 'false', description: language === 'it' ? 'Attendi completamento (max 5 min)' : 'Wait for completion (max 5 min)' },
      ],
      example: `curl -X POST "${baseUrl}/analyze" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "prompt": "Voglio creare un app mobile per gestire le spese personali",
    "language": "it",
    "team_size": "medium",
    "wait_for_completion": true
  }'`
    },
    {
      method: 'GET',
      path: '/projects',
      description: language === 'it' ? 'Lista i tuoi progetti' : 'List your projects',
      params: [
        { name: 'limit', type: 'integer', required: false, default: '20', description: 'Max 100' },
        { name: 'skip', type: 'integer', required: false, default: '0', description: 'Offset' },
      ],
      example: `curl -X GET "${baseUrl}/projects?limit=10" \\
  -H "Authorization: Bearer YOUR_API_KEY"`
    },
    {
      method: 'GET',
      path: '/projects/{project_id}',
      description: language === 'it' ? 'Dettagli di un progetto' : 'Get project details',
      params: [],
      example: `curl -X GET "${baseUrl}/projects/PROJECT_ID" \\
  -H "Authorization: Bearer YOUR_API_KEY"`
    },
    {
      method: 'POST',
      path: '/projects/{project_id}/execute',
      description: language === 'it' ? 'Esegui un progetto in fase di configurazione' : 'Execute a project in configuration phase',
      params: [
        { name: 'wait_for_completion', type: 'boolean', required: false, default: 'false', description: language === 'it' ? 'Attendi completamento' : 'Wait for completion' },
      ],
      example: `curl -X POST "${baseUrl}/projects/PROJECT_ID/execute" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"wait_for_completion": true}'`
    },
    {
      method: 'POST',
      path: '/projects/{project_id}/chat',
      description: language === 'it' ? 'Chatta con l\'analista su un progetto' : 'Chat with the analyzer about a project',
      params: [
        { name: 'message', type: 'string', required: true, description: language === 'it' ? 'Il tuo messaggio' : 'Your message' },
      ],
      example: `curl -X POST "${baseUrl}/projects/PROJECT_ID/chat" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Quali sono i rischi principali?"}'`
    },
    {
      method: 'GET',
      path: '/custom-experts',
      description: language === 'it' ? 'Lista esperti custom' : 'List custom experts',
      params: [],
      example: `curl -X GET "${baseUrl}/custom-experts" \\
  -H "Authorization: Bearer YOUR_API_KEY"`
    },
    {
      method: 'POST',
      path: '/custom-experts',
      description: language === 'it' ? 'Crea un esperto custom' : 'Create a custom expert',
      params: [
        { name: 'name', type: 'string', required: true, description: language === 'it' ? 'Nome dell\'esperto' : 'Expert name' },
        { name: 'description', type: 'string', required: true, description: language === 'it' ? 'Descrizione dell\'esperto' : 'Expert description' },
        { name: 'expertise_area', type: 'string', required: true, description: language === 'it' ? 'Area di competenza' : 'Expertise area' },
        { name: 'is_global', type: 'boolean', required: false, default: 'false', description: language === 'it' ? 'Disponibile globalmente' : 'Available globally' },
        { name: 'preferred_model', type: 'string', required: false, description: language === 'it' ? 'Modello LLM preferito' : 'Preferred LLM model' },
      ],
      example: `curl -X POST "${baseUrl}/custom-experts" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Esperto Blockchain",
    "description": "Specialista in tecnologie blockchain e smart contracts",
    "expertise_area": "Blockchain e DeFi",
    "is_global": true
  }'`
    },
    {
      method: 'DELETE',
      path: '/custom-experts/{expert_id}',
      description: language === 'it' ? 'Elimina un esperto custom' : 'Delete a custom expert',
      params: [],
      example: `curl -X DELETE "${baseUrl}/custom-experts/EXPERT_ID" \\
  -H "Authorization: Bearer YOUR_API_KEY"`
    },
    {
      method: 'POST',
      path: '/projects/{project_id}/answer',
      description: language === 'it' ? 'Rispondi alle domande di chiarificazione' : 'Answer clarification questions',
      params: [
        { name: 'answers', type: 'array', required: true, description: language === 'it' ? 'Array di {question_id, answer}' : 'Array of {question_id, answer}' },
      ],
      example: `curl -X POST "${baseUrl}/projects/PROJECT_ID/answer" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "answers": [
      {"question_id": "q1", "answer": "Il budget è di 50.000€"},
      {"question_id": "q2", "answer": "Timeline: 6 mesi"}
    ]
  }'`
    },
    {
      method: 'DELETE',
      path: '/projects/{project_id}',
      description: language === 'it' ? 'Elimina un progetto' : 'Delete a project',
      params: [],
      example: `curl -X DELETE "${baseUrl}/projects/PROJECT_ID" \\
  -H "Authorization: Bearer YOUR_API_KEY"`
    },
    {
      method: 'GET',
      path: '/models',
      description: language === 'it' ? 'Lista modelli LLM disponibili' : 'List available LLM models',
      params: [],
      example: `curl -X GET "${baseUrl}/models" \\
  -H "Authorization: Bearer YOUR_API_KEY"`
    },
  ];

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
            <Book className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold">
              {language === 'it' ? 'Documentazione API' : 'API Documentation'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'it' 
                ? 'Integra LLM Council nelle tue applicazioni' 
                : 'Integrate LLM Council into your applications'}
            </p>
          </div>
        </div>

        {/* API Keys Section */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Key className="w-5 h-5 text-primary" />
                <div>
                  <CardTitle className="font-heading">API Keys</CardTitle>
                  <CardDescription>
                    {language === 'it' 
                      ? 'Gestisci le tue chiavi API per l\'accesso programmatico' 
                      : 'Manage your API keys for programmatic access'}
                  </CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Create new key - now automatic */}
            <div className="flex gap-2 items-center">
              <Button onClick={createApiKey} disabled={creating} className="gap-2">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {language === 'it' ? 'Crea nuova API Key' : 'Create new API Key'}
              </Button>
              <span className="text-sm text-muted-foreground">
                {language === 'it' 
                  ? 'La chiave verrà generata automaticamente in modo sicuro' 
                  : 'The key will be automatically generated securely'}
              </span>
            </div>

            {/* Existing keys */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {language === 'it' ? 'Nessuna API key creata' : 'No API keys created'}
              </div>
            ) : (
              <div className="space-y-2">
                {apiKeys.map((key) => (
                  <div key={key.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-background">
                    <div className="flex-1">
                      <p className="font-medium">{key.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        llmc_{key.key_prefix}...
                      </p>
                      {key.last_used && (
                        <p className="text-xs text-muted-foreground">
                          {language === 'it' ? 'Ultimo uso:' : 'Last used:'} {new Date(key.last_used).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Copy prefix button */}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => copyToClipboard(`llmc_${key.key_prefix}...`)}
                        title={language === 'it' ? 'Copia prefisso' : 'Copy prefix'}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      {/* Delete with confirmation */}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-card border-border">
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {language === 'it' ? 'Eliminare questa API key?' : 'Delete this API key?'}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {language === 'it' 
                                ? `Sei sicuro di voler eliminare la chiave "${key.name}"? Questa azione non può essere annullata e le applicazioni che usano questa chiave smetteranno di funzionare.`
                                : `Are you sure you want to delete the key "${key.name}"? This action cannot be undone and applications using this key will stop working.`}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>
                              {language === 'it' ? 'Annulla' : 'Cancel'}
                            </AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => deleteApiKey(key.id)} 
                              className="bg-destructive hover:bg-destructive/90"
                            >
                              {language === 'it' ? 'Elimina' : 'Delete'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Authentication */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Terminal className="w-5 h-5 text-info" />
              <div>
                <CardTitle className="font-heading">
                  {language === 'it' ? 'Autenticazione' : 'Authentication'}
                </CardTitle>
                <CardDescription>
                  {language === 'it' 
                    ? 'Come autenticarti alle API' 
                    : 'How to authenticate to the API'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="p-4 rounded-lg bg-muted/30 border border-border font-mono text-sm">
              <p className="text-muted-foreground mb-2"># {language === 'it' ? 'Aggiungi header Authorization' : 'Add Authorization header'}</p>
              <p>Authorization: Bearer YOUR_API_KEY</p>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              {language === 'it' 
                ? 'Le API key iniziano con "llmc_". Tieni la tua chiave al sicuro e non condividerla.' 
                : 'API keys start with "llmc_". Keep your key safe and do not share it.'}
            </p>
          </CardContent>
        </Card>

        {/* Endpoints */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Code className="w-5 h-5 text-success" />
              <div>
                <CardTitle className="font-heading">Endpoints</CardTitle>
                <CardDescription>Base URL: {baseUrl}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {endpoints.map((endpoint, idx) => (
              <div key={idx} className="p-4 rounded-lg border border-border bg-background">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                    endpoint.method === 'GET' ? 'bg-info/20 text-info' : 'bg-success/20 text-success'
                  }`}>
                    {endpoint.method}
                  </span>
                  <code className="text-sm font-mono">{endpoint.path}</code>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{endpoint.description}</p>
                
                {endpoint.params.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-medium mb-2 text-muted-foreground uppercase">
                      {language === 'it' ? 'Parametri' : 'Parameters'}
                    </p>
                    <div className="space-y-1">
                      {endpoint.params.map((param, pidx) => (
                        <div key={pidx} className="flex items-start gap-2 text-sm">
                          <code className="text-primary">{param.name}</code>
                          <span className="text-muted-foreground">({param.type})</span>
                          {param.required && <span className="text-destructive text-xs">*</span>}
                          {param.default && <span className="text-muted-foreground text-xs">default: {param.default}</span>}
                          <span className="text-muted-foreground">- {param.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase">
                      {language === 'it' ? 'Esempio' : 'Example'}
                    </p>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(endpoint.example)}>
                      <Copy className="w-3 h-3 mr-1" />
                      {language === 'it' ? 'Copia' : 'Copy'}
                    </Button>
                  </div>
                  <pre className="p-3 rounded-lg bg-muted/50 text-xs overflow-x-auto font-mono">
                    {endpoint.example}
                  </pre>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* New Key Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-success" />
              {language === 'it' ? 'API Key Creata' : 'API Key Created'}
            </DialogTitle>
            <DialogDescription>
              {language === 'it' 
                ? 'Copia questa chiave ora. Non sarà più visibile.' 
                : 'Copy this key now. It won\'t be shown again.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Input
                type={showKey ? 'text' : 'password'}
                value={newKeyValue}
                readOnly
                className="font-mono"
              />
              <Button variant="ghost" size="icon" onClick={() => setShowKey(!showKey)}>
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              <Button variant="outline" onClick={() => copyToClipboard(newKeyValue)}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <Button className="w-full" onClick={() => setShowKeyDialog(false)}>
              {language === 'it' ? 'Ho copiato la chiave' : 'I\'ve copied the key'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
